import { combine, createEvent, sample } from 'effector'
import { and, not } from 'patronum'
import { model as i18n } from '~/core/i18n'
import { register } from '~/core/repository'
import { model as router } from '~/core/router'
import { entityPartnerProductDetailsModel } from '~/entities/partner-product/details'
import { model as paymentModel } from '~/features/payment'
import { model as guestModel } from '~/features/subscriber/guest'
import { featureSubscriberPartnerProgramModel } from '~/features/subscriber/partner-program'
import { aye } from '~/shared/helpers'
import { partnerProgramGoPopupModel } from '~/widgets/PartnerProgram/PartnerProgramGoPopup'

interface InitLeftParams {
  partnerProductId: number
  query: URLSearchParams
}
export const init = createEvent<InitLeftParams>()
export const left = createEvent<InitLeftParams>()

// navigate to home if partners program is not enabled
sample({
  clock: init,
  filter: not(featureSubscriberPartnerProgramModel.$partnersProgramEnabled),
  fn: () => '/',
  target: router.navigatePush,
})

sample({
  clock: init,
  filter: and(
    not(entityPartnerProductDetailsModel.$partnerProductDetailsLoading),
    featureSubscriberPartnerProgramModel.$partnersProgramEnabled
  ),
  fn: ({ partnerProductId }) => ({ partnerProductId }),
  target: entityPartnerProductDetailsModel.loadPartnerProductDetails,
})

sample({
  clock: left,
  filter: featureSubscriberPartnerProgramModel.$partnersProgramEnabled,
  target: entityPartnerProductDetailsModel.resetPartnerProductDetails,
})

const $partnerProductDetailsTranslations = combine(
  entityPartnerProductDetailsModel.$partnerProductDetailsFormatted,
  i18n.$t,
  (partnerProduct, { t }) => {
    const isRentedOrFreeAndNotActivated =
      (partnerProduct?.rented || partnerProduct?.free) &&
      !partnerProduct.isActive

    return {
      title: isRentedOrFreeAndNotActivated
        ? t('pending_activation_header')
        : partnerProduct?.name,
      text: isRentedOrFreeAndNotActivated
        ? t('pending_activation_description')
        : partnerProduct?.description,
      titleButton: t('more'),
      goToApp: t('go_to_app'),
    }
  }
)

const $isShowPaymentButton = combine(
  entityPartnerProductDetailsModel.$partnerProductDetailsFormatted,
  (partnerProduct) => !partnerProduct?.rented && !partnerProduct?.free
)

const $isShowGoToAppButton = combine(
  entityPartnerProductDetailsModel.$partnerProductDetailsFormatted,
  (partnerProduct) =>
    aye(
      (partnerProduct?.rented || partnerProduct?.free) &&
        partnerProduct.isActive
    )
)

const $showHowToWatchButton = combine(
  $isShowPaymentButton,
  paymentModel.$externalPaymentEnabled,
  guestModel.$guest,
  (isShowPaymentButton, externalPaymentEnabled, guest) =>
    isShowPaymentButton && externalPaymentEnabled && !guest
)

const $showSignInToWatchButton = combine(
  $isShowPaymentButton,
  guestModel.$guest,
  (isShowPaymentButton, guest) => isShowPaymentButton && guest
)

register(
  entityPartnerProductDetailsModel.$partnerProductDetailsFormatted,
  '$partnerProgram/details/partnerProductDetailsFormatted'
)
register(
  entityPartnerProductDetailsModel.$partnerProductDetailsLoading,
  '$partnerProgram/details/partnerProductDetailsLoading'
)
register(
  $partnerProductDetailsTranslations,
  '$partnerProductDetailsTranslations'
)
register(partnerProgramGoPopupModel.selectGoToApp, 'selectGoToApp')
register($isShowPaymentButton, '$isShowPaymentButton')
register($isShowGoToAppButton, '$isShowGoToAppButton')

register($showHowToWatchButton, '$partner-program/showHowToWatchButton')
register($showSignInToWatchButton, '$partner-program/showSignInToWatchButton')
